import React from 'react';
import { Info } from 'lucide-react';

/**
 * Компонент с информацией об ипотеке
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об ипотеке: условия, программы, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3>Что такое ипотека?</h3>
        <p>
          <strong>Ипотека</strong> — это целевой долгосрочный кредит на покупку недвижимости (квартиры, дома, апартаментов), где залогом для банка выступает сама приобретаемая недвижимость. Если заемщик перестает выплачивать кредит, банк имеет право реализовать залоговое имущество для покрытия долга. Это один из самых востребованных способов приобретения жилья в России, позволяющий стать собственником недвижимости, не имея всей суммы сразу.
        </p>

        <h3>Ключевые параметры ипотечного кредита</h3>
        <ul>
          <li><strong>Сумма кредита:</strong> Разница между стоимостью недвижимости и первоначальным взносом.</li>
          <li><strong>Процентная ставка:</strong> Плата за пользование кредитными средствами (% годовых). Может быть <em>фиксированной</em> или <em>плавающей</em>.</li>
          <li><strong>Срок кредита:</strong> Обычно от 3 до 30 лет (иногда до 50). Длиннее срок = ниже платеж, но больше переплата.</li>
          <li><strong>Первоначальный взнос:</strong> Часть стоимости, оплачиваемая заемщиком (обычно от 15-20%). Больше взнос = лучше условия.</li>
          <li>
            <strong>Тип платежа:</strong>
            <ul>
              <li><strong>Аннуитетный:</strong> Равные ежемесячные платежи. В начале больше %, к концу — основного долга. Проще планировать.</li>
              <li><strong>Дифференцированный:</strong> Платежи уменьшаются. Основной долг гасится равными долями, % начисляются на остаток. Переплата ниже, но первые платежи выше.</li>
            </ul>
          </li>
        </ul>

        <h3>Государственные и льготные программы ипотеки в России (~2024-2025 гг.)</h3>
        <p>Программы с субсидированными ставками:</p>
        <ul>
          <li><strong>Господдержка (новостройки):</strong> Ставка ~8%.</li>
          <li><strong>Семейная:</strong> Для семей с детьми, ставка ~6%.</li>
          <li><strong>Дальневосточная / Арктическая:</strong> Ставка до 2%.</li>
          <li><strong>IT-ипотека:</strong> Для сотрудников IT-компаний, ставка ~5%.</li>
          <li><strong>Сельская:</strong> Для жилья в селах (до 30 тыс. чел.), ставка до 3%.</li>
          <li><strong>Военная:</strong> Для участников НИС.</li>
        </ul>
        <p><em>Важно: Условия могут меняться. Уточняйте в банках.</em></p>

        <h3>Досрочное погашение и рефинансирование</h3>
        <ul>
          <li>
            <strong>Досрочное погашение:</strong> Внесение суммы сверх платежа (без комиссий). Можно выбрать:
            <ul>
              <li><strong>Уменьшение срока:</strong> Платеж тот же, срок короче (выгоднее по %%).</li>
              <li><strong>Уменьшение платежа:</strong> Срок тот же, нагрузка ниже.</li>
            </ul>
            Наш калькулятор моделирует оба варианта.
          </li>
          <li>
            <strong>Рефинансирование:</strong> Новый кредит для погашения старого на лучших условиях (ниже ставка). Целесообразно при снижении ставок на 1-2%+, особенно в первой половине срока. Учитывайте расходы на переоформление.
          </li>
        </ul>

        <h3>Дополнительные расходы</h3>
        <ul>
          <li><strong>Страхование:</strong> Имущества (обязательно), жизни/здоровья (влияет на ставку), титула (для вторички).</li>
          <li><strong>Оценка недвижимости.</strong></li>
          <li><strong>Госпошлины.</strong></li>
          <li><strong>Нотариальные услуги.</strong></li>
          <li><strong>Банковские комиссии.</strong></li>
        </ul>
        <p>Наш калькулятор позволяет учесть страховку и разовые расходы.</p>

        <h3>Как сделать ипотеку выгоднее?</h3>
        <ul>
          <li>Сравнивайте банки и программы.</li>
          <li>Используйте льготы и маткапитал.</li>
          <li>Максимальный первоначальный взнос.</li>
          <li>Досрочное погашение (с уменьшением срока).</li>
          <li>Рассмотрите рефинансирование при снижении ставок.</li>
          <li>Оформите налоговый вычет (до 260 тыс. ₽ за покупку + до 390 тыс. ₽ за проценты).</li>
        </ul>

        <h3>Ипотечная ставка и ключевая ставка ЦБ</h3>
        <p>
          Ипотечные ставки зависят от <strong>ключевой ставки ЦБ</strong>, но также от инфляции, рисков, спроса, конкуренции и льготных программ.
        </p>

        <h3>Что делать при финансовых трудностях?</h3>
        <p>Сразу обращайтесь в банк, не допуская просрочек.</p>
        <ul>
          <li><strong>Ипотечные каникулы:</strong> Отсрочка/уменьшение платежей до 6 мес. по закону при трудных ситуациях.</li>
          <li><strong>Реструктуризация:</strong> Индивидуальное изменение условий с банком.</li>
          <li><strong>Рефинансирование.</strong></li>
          <li><strong>Продажа недвижимости (с согласия банка).</strong></li>
        </ul>

        <p className="font-semibold mt-6">
          Наш ипотечный калькулятор — ваш надежный помощник в планировании. Моделируйте разные сценарии и выбирайте оптимальное решение!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;